import { apiClient } from "@/lib/api/client";
import { env, setSessionProvider } from "@/lib/env";
import type { SupportedLanguage } from "@/lib/i18n";
import { isSupportedLanguage } from "@/lib/i18n";
import { injectLogContext } from "@/lib/logger";
import { queryClient } from "@/lib/queryClient";
import { resetAllStores } from "@/lib/store/resettableStore";
import type { ReturnOf } from "@dresscode-frontend/utility-types";
import { fetchAuthSession, getCurrentUser, signOut } from "aws-amplify/auth";
import { produce } from "immer";
import { create } from "zustand";
import type { StoreApi } from "zustand";
import { persist } from "zustand/middleware";
import {
  confirmResetPassword,
  confirmSignIn,
  resendSignInCode,
  resetPassword,
  signIn,
} from "./operations";
import {
  type AccessToken,
  type ConfirmResetPasswordForm,
  type ConfirmSignInForm,
  type IdToken,
  MfaResNextStep,
  type SignInForm,
  type UserSession,
} from "./types";
import { authStoreSchema, tokenSchema, userSessionSchema } from "./zod-schema";

export { MfaResNextStep };
export type {
  AccessToken,
  ConfirmResetPasswordForm,
  ConfirmSignInForm,
  IdToken,
  SignInForm,
  UserSession,
};

export type SignInResult = ReturnOf<typeof signIn> & {
  session: UserSession | undefined;
};

export type ConfirmSignInResult = ReturnOf<typeof confirmSignIn> & {
  session: UserSession;
};

type AuthStore = {
  accessToken: AccessToken | null;
  accessTokenExp: number | null;
  idToken: IdToken | null;
  idTokenExp: number | null;
  session: UserSession | null;
  locale: SupportedLanguage | null;
  setLocale: (locale: SupportedLanguage) => void;
  isAuthenticated: () => Promise<boolean>;
  getAccessToken: () => Promise<AccessToken | null>;
  getSession: () => Promise<UserSession>;
  refreshCognitoTokens: () => Promise<{
    accessToken: AccessToken | null;
    idToken: IdToken | null;
  }>;
  refreshUserSession: () => Promise<UserSession>;
  signIn: (form: SignInForm) => Promise<SignInResult>;
  confirmSignIn: (form: ConfirmSignInForm) => Promise<ConfirmSignInResult>;
  resendSignInCode: (
    signInFormData: SignInForm,
  ) => Promise<ReturnType<typeof resendSignInCode>>;
  logout: () => Promise<void>;
  downloadSignedCookies: () => Promise<void>;
  resetPassword: (
    username: string,
  ) => Promise<ReturnType<typeof resetPassword>>;
  confirmResetPassword: (
    form: ConfirmResetPasswordForm,
  ) => Promise<ReturnType<typeof confirmResetPassword>>;
};

function createAccessToken(token: string): AccessToken {
  tokenSchema.parse(token);
  return token as AccessToken;
}

function createIdToken(token: string): IdToken {
  tokenSchema.parse(token);
  return token as IdToken;
}

/**
 * APIからセッション情報を取得してストアを更新するヘルパー関数
 */
async function fetchAndUpdateSession(
  apiClient: typeof import("@/lib/api/client").apiClient,
  userSessionSchema: typeof import("./zod-schema").userSessionSchema,
  set: StoreApi<AuthStore>["setState"],
): Promise<UserSession | null> {
  const res = await apiClient.GET("/v2/session");
  if (!res.data) {
    return null;
  }
  const session = userSessionSchema.parse(res.data);
  set(
    produce((draft: AuthStore) => {
      draft.session = session;
    }),
  );
  return session;
}

/**
 * ユーザーセッションをリフレッシュし、locale同期も処理するヘルパー関数
 */
async function refreshSession(
  apiClient: typeof import("@/lib/api/client").apiClient,
  userSessionSchema: typeof import("./zod-schema").userSessionSchema,
  set: StoreApi<AuthStore>["setState"],
): Promise<UserSession> {
  const session = await fetchAndUpdateSession(
    apiClient,
    userSessionSchema,
    set,
  );
  if (!session) {
    // エラーの場合、後続の処理が実行できないため、throwする
    throw new Error("Failed to fetch user session from API");
  }

  // 認証前にクライアントで設定されたlocaleがある場合、サーバーのsession.localeと同期
  const currentState = useAuthStore.getState();
  if (currentState.locale && currentState.locale !== session.locale) {
    try {
      // updateLocaleを使用してクライアントのlocaleをサーバーに同期
      const { updateLocale } = await import("../i18n/hooks/useUpdateLocale");
      await updateLocale(
        currentState.locale,
        {
          isAuthenticated: true,
          setLocale: useAuthStore.getState().setLocale,
          refreshUserSession: async () => {
            // 再帰呼び出しを避けるため、APIを直接呼び出し
            await fetchAndUpdateSession(apiClient, userSessionSchema, set);
          },
        },
        {
          apiClient: {
            PATCH: apiClient.PATCH,
          },
        },
      );

      // 同期後のセッション情報を取得
      const updatedState = useAuthStore.getState();
      if (updatedState.session) {
        return updatedState.session;
      }
    } catch (error) {
      console.warn("Failed to sync client locale to session:", error);
      // 同期に失敗してもセッション情報は返す
    }
  }

  return session;
}

let refreshPromise: Promise<{
  accessToken: AccessToken | null;
  idToken: IdToken | null;
}> | null = null;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => {
      const store = {
        accessToken: null,
        accessTokenExp: null,
        idToken: null,
        idTokenExp: null,
        session: null,
        locale: null,
        setLocale: (locale: string): void => {
          if (!isSupportedLanguage(locale)) {
            throw new Error(`Unsupported locale: ${locale}`);
          }
          set(
            produce((draft: AuthStore) => {
              draft.locale = locale;
            }),
          );
        },
        isAuthenticated: async (): Promise<boolean> => {
          try {
            await getCurrentUser();
            return true;
          } catch (error) {
            console.warn("isAuthenticated failed", error);
            return false;
          }
        },
        getAccessToken: async (): Promise<AccessToken | null> => {
          const { accessToken, accessTokenExp } = get();
          // accessTokenExp は秒単位、Date.now()はミリ秒単位
          const now = Math.floor(Date.now() / 1000);
          if (accessTokenExp && accessTokenExp < now) {
            console.warn("accessToken expired");
            /**
             * NOTE: refreshの状態を追跡する
             *  - refreshCognitoTokens の実行状態を管理するPromiseを保持し、refresh処理が実行中の場合はそのPromiseを共有
             * NOTE: mutexとか使って排他制御するのもありだが、そこまでする必要があるかは要検討
             *  - サンプル
             *  - const mutex = new Mutex();
             *  - mutex.runExclusive(async () => {
             *  -   if (get().accessTokenExp && get().accessTokenExp > Date.now()) {
             *  -     return;
             *  -   }
             *  -   await get().refreshCognitoTokens();
             *  - });
             */
            if (refreshPromise === null) {
              refreshPromise = get()
                .refreshCognitoTokens()
                .finally(() => {
                  refreshPromise = null;
                });
            }
            await refreshPromise;
            return get().accessToken;
          }
          return accessToken;
        },
        getSession: async (): Promise<UserSession> => {
          const { session, refreshUserSession } = get();
          if (session) {
            injectLogContext({
              dressCodeUserId: session.user.id,
              tenantId: session.organization.tenantId,
              organizationId: session.organization.organizationId,
            });
            setSessionProvider({
              getSession: () => ({
                organizationId: session.organization.organizationId,
              }),
            });
            return session;
          }
          return await refreshUserSession();
        },
        refreshCognitoTokens: async (): Promise<{
          accessToken: AccessToken | null;
          idToken: IdToken | null;
        }> => {
          const session = await fetchAuthSession({ forceRefresh: true });
          if (!session || !session.tokens) {
            throw new Error("Failed to fetch session tokens");
          }
          const accessToken = createAccessToken(
            session.tokens.accessToken.toString(),
          );
          const idToken = session.tokens.idToken
            ? createIdToken(session.tokens.idToken.toString())
            : null;
          set(
            produce((draft: AuthStore) => {
              draft.accessToken = accessToken;
              draft.accessTokenExp =
                session?.tokens?.accessToken.payload.exp ?? null;
              draft.idToken = idToken;
              draft.idTokenExp = session?.tokens?.idToken?.payload.exp ?? null;
            }),
          );
          return { accessToken, idToken };
        },
        refreshUserSession: async (): Promise<UserSession> => {
          return await refreshSession(apiClient, userSessionSchema, set);
        },
        signIn: async (form: SignInForm): Promise<SignInResult> => {
          const result = await signIn({
            username: form.username.trim(),
            password: form.password,
            options: { authFlowType: "USER_SRP_AUTH" },
          });
          if (result.error) {
            // ログイン画面のエラーメッセージ表示用ErrorInstanceの作成
            const error = new Error(result.error.message || "SignIn failed");
            error.name = result.error.name || "SignInError";
            throw error;
          }

          const signInStep = result.data?.nextStep
            .signInStep as keyof typeof MfaResNextStep.signInStep;
          if (!Object.values(MfaResNextStep.signInStep).includes(signInStep)) {
            const { refreshCognitoTokens, downloadSignedCookies, getSession } =
              get();
            await refreshCognitoTokens();
            await downloadSignedCookies();
            const session = await getSession();
            return { ...result, session };
          }

          return { ...result, session: undefined };
        },
        confirmSignIn: async (
          form: ConfirmSignInForm,
        ): Promise<ConfirmSignInResult> => {
          const result = await confirmSignIn(form);
          if (result.error) {
            const error = new Error(
              result.error.message || "Confirm sign in failed",
            );
            error.name = result.error.name || "ConfirmSignInError";
            throw error;
          }

          const { refreshCognitoTokens, downloadSignedCookies, getSession } =
            get();
          await refreshCognitoTokens();
          await downloadSignedCookies();
          const session = await getSession();

          return { ...result, session };
        },
        resendSignInCode: async (
          signInFormData: SignInForm,
        ): Promise<ReturnType<typeof resendSignInCode>> => {
          const result = await resendSignInCode(signInFormData);
          return result;
        },
        logout: async (): Promise<void> => {
          resetAllStores();
          setSessionProvider(null);
          await signOut();

          // NOTE:　router非依存にするため、window.location.hrefを使用
          window.location.href = "/auth/sign-in";

          // ログアウト時にクエリキャッシュをクリア
          await queryClient.cancelQueries();
          queryClient.clear();
          set(
            produce((draft: AuthStore) => {
              draft.accessToken = null;
              draft.idToken = null;
              draft.session = null;
            }),
          );
        },
        downloadSignedCookies: async (): Promise<void> => {
          const { idToken } = get();
          if (!idToken) throw new Error("ID token is missing");

          if (env.VITE_SET_ASSET_COOKIE_SKIP) return;

          const response = await fetch(
            env.VITE_SET_ASSET_COOKIE_URL || "/api/set-cookie",
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${idToken}`,
              },
              credentials: "include",
            },
          );
          if (!response.ok) {
            throw new Error("Failed to download signed cookies");
          }
        },
        resetPassword: async (
          username: string,
        ): Promise<ReturnType<typeof resetPassword>> => {
          const result = await resetPassword({ username: username.trim() });
          if (result.error) {
            const error = new Error(
              result.error.message || "Reset password failed",
            );
            error.name = result.error.name || "ResetPasswordError";
            throw error;
          }
          if (
            result.data?.nextStep.resetPasswordStep ===
            "CONFIRM_RESET_PASSWORD_WITH_CODE"
          ) {
            return result;
          }
          throw new Error("Failed to reset password");
        },
        confirmResetPassword: async (
          form: ConfirmResetPasswordForm,
        ): Promise<ReturnType<typeof confirmResetPassword>> => {
          const result = await confirmResetPassword(form);
          if (result.error) {
            const error = new Error(
              result.error.message || "Reset password failed",
            );
            error.name = result.error.name || "ResetPasswordError";
            throw error;
          }
          return result;
        },
      };
      authStoreSchema.parse(store);
      return store;
    },
    { name: "auth", version: 1 },
  ),
);
